import {
  type CompanyProfile,
  type CompanySettings,
  FAVICON_MIME_TYPES,
  LOGO_MAX_BYTES,
  LOGO_MIME_TYPES,
  type LogoInfo,
  type LogoVariant,
  type OrganizationMode,
  displayPhone,
  formatCpfOrCnpj,
  formatPhone,
} from '@pm/schemas';
import { Button, cn, ErrorState, Field, Input, LabelCaps, SkeletonRows } from '@pm/ui';
import { ImageOff, Trash2, Upload } from 'lucide-react';
import { type ReactNode, useRef, useState } from 'react';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { settingsKeys, useCompanySettings } from '@/lib/branding';
import { useInvalidating } from '@/lib/queries';
import { useToast } from '@/lib/toast';

const EMPTY: CompanyProfile = {
  legal_name: null,
  trade_name: null,
  document: null,
  state_registration: null,
  municipal_registration: null,
  email: null,
  phone: null,
  website: null,
  zip_code: null,
  street: null,
  number: null,
  complement: null,
  district: null,
  city: null,
  state: null,
};

/** Administração → "Dados da Empresa": cadastro da empresa titular do sistema e logotipos. */
export function CompanySection() {
  const { data, isLoading, isError, error, refetch } = useCompanySettings();
  if (isLoading) return <SkeletonRows rows={6} />;
  if (isError || !data)
    return <ErrorState message={error?.message} onRetry={() => void refetch()} />;
  return (
    <div className="grid items-start gap-gap-lg xl:grid-cols-[1fr_360px]">
      <div className="flex flex-col gap-gap-lg">
        <ProfileForm key={data.updated_at ?? 'novo'} settings={data} />
        <ModeCard key={data.mode} mode={data.mode} />
      </div>
      <div className="flex flex-col gap-gap-lg">
        <LogosCard settings={data} />
        <FaviconCard settings={data} />
      </div>
    </div>
  );
}

function ProfileForm({ settings }: { settings: CompanySettings }) {
  const toast = useToast();
  // Documento e telefone já formatados (registros antigos podem estar só com dígitos).
  const initial: CompanyProfile = {
    ...EMPTY,
    ...settings.profile,
    document: settings.profile.document ? formatCpfOrCnpj(settings.profile.document) : null,
    phone: settings.profile.phone ? displayPhone(settings.profile.phone) : null,
  };
  const [form, setForm] = useState<CompanyProfile>(initial);
  const dirty = JSON.stringify(form) !== JSON.stringify(initial);
  const save = useInvalidating(
    (body: CompanyProfile) => api.put<CompanySettings>('/organization/company/profile', body),
    [settingsKeys.company, settingsKeys.brandingAll],
  );
  const bind = (key: keyof CompanyProfile, mask?: (v: string) => string) => ({
    id: `co-${key}`,
    value: form[key] ?? '',
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = mask ? mask(e.target.value) : e.target.value;
      setForm((f) => ({ ...f, [key]: value }));
    },
  });

  return (
    <form
      className="flex flex-col gap-4 rounded-md border border-border bg-surface-1 p-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate(form, {
          onSuccess: () => toast.success('Dados da empresa salvos'),
          onError: (err) => toast.error(err),
        });
      }}
    >
      <div>
        <LabelCaps>Dados cadastrais</LabelCaps>
        <p className="mt-1 text-sm text-text-3">
          Empresa titular do sistema. O nome fantasia aparece no topo do painel quando não há
          logotipo.
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-6">
        <Cell span={6}>
          <Field label="Razão social" htmlFor="co-legal_name">
            <Input {...bind('legal_name')} maxLength={200} />
          </Field>
        </Cell>
        <Cell span={4}>
          <Field label="Nome fantasia" htmlFor="co-trade_name">
            <Input {...bind('trade_name')} maxLength={200} />
          </Field>
        </Cell>
        <Cell span={2}>
          <Field label="CNPJ / CPF" htmlFor="co-document">
            <Input
              {...bind('document', formatCpfOrCnpj)}
              mono
              maxLength={18}
              placeholder="00.000.000/0000-00"
            />
          </Field>
        </Cell>
        <Cell span={3}>
          <Field label="Inscrição estadual" htmlFor="co-state_registration">
            <Input {...bind('state_registration')} mono maxLength={32} />
          </Field>
        </Cell>
        <Cell span={3}>
          <Field label="Inscrição municipal" htmlFor="co-municipal_registration">
            <Input {...bind('municipal_registration')} mono maxLength={32} />
          </Field>
        </Cell>
        <Cell span={3}>
          <Field label="E-mail" htmlFor="co-email">
            <Input {...bind('email')} type="email" maxLength={254} />
          </Field>
        </Cell>
        <Cell span={3}>
          <Field label="Telefone" htmlFor="co-phone">
            <Input
              {...bind('phone', formatPhone)}
              type="tel"
              mono
              maxLength={19}
              placeholder="(11) 3456-7890"
            />
          </Field>
        </Cell>
        <Cell span={6}>
          <Field label="Site" htmlFor="co-website">
            <Input {...bind('website')} mono maxLength={200} placeholder="https://" />
          </Field>
        </Cell>
      </div>

      <LabelCaps className="mt-1">Endereço</LabelCaps>
      <div className="grid gap-3 sm:grid-cols-6">
        <Cell span={2}>
          <Field label="CEP" htmlFor="co-zip_code">
            <Input {...bind('zip_code')} mono maxLength={12} placeholder="00000-000" />
          </Field>
        </Cell>
        <Cell span={4}>
          <Field label="Logradouro" htmlFor="co-street">
            <Input {...bind('street')} maxLength={200} />
          </Field>
        </Cell>
        <Cell span={2}>
          <Field label="Número" htmlFor="co-number">
            <Input {...bind('number')} mono maxLength={20} />
          </Field>
        </Cell>
        <Cell span={4}>
          <Field label="Complemento" htmlFor="co-complement">
            <Input {...bind('complement')} maxLength={100} />
          </Field>
        </Cell>
        <Cell span={3}>
          <Field label="Bairro" htmlFor="co-district">
            <Input {...bind('district')} maxLength={100} />
          </Field>
        </Cell>
        <Cell span={2}>
          <Field label="Cidade" htmlFor="co-city">
            <Input {...bind('city')} maxLength={100} />
          </Field>
        </Cell>
        <Cell span={1}>
          <Field label="UF" htmlFor="co-state">
            <Input
              {...bind('state')}
              mono
              maxLength={2}
              onChange={(e) => setForm((f) => ({ ...f, state: e.target.value.toUpperCase() }))}
            />
          </Field>
        </Cell>
      </div>

      <div className="-mx-4 -mb-4 mt-1 flex items-center justify-end gap-2 border-t border-border px-4 py-3">
        {dirty ? (
          <Button
            type="button"
            variant="ghost"
            onClick={() => setForm({ ...EMPTY, ...settings.profile })}
          >
            Descartar
          </Button>
        ) : null}
        <Button type="submit" variant="primary" loading={save.isPending} disabled={!dirty}>
          Salvar
        </Button>
      </div>
    </form>
  );
}

function Cell({ span, children }: { span: 1 | 2 | 3 | 4 | 6; children: ReactNode }) {
  const cls = {
    1: 'sm:col-span-1',
    2: 'sm:col-span-2',
    3: 'sm:col-span-3',
    4: 'sm:col-span-4',
    6: 'sm:col-span-6',
  }[span];
  return <div className={cls}>{children}</div>;
}

function LogosCard({ settings }: { settings: CompanySettings }) {
  const { logos } = settings.branding;
  return (
    <div className="flex flex-col gap-4 rounded-md border border-border bg-surface-1 p-4">
      <div>
        <LabelCaps>Logotipos</LabelCaps>
        <p className="mt-1 text-sm text-text-3">
          Exibidos no topo do painel conforme o tema. Se enviar só um, ele vale para os dois.
        </p>
      </div>
      <LogoSlot variant="dark" label="Tema escuro" logo={logos.dark} />
      <LogoSlot variant="light" label="Tema claro" logo={logos.light} />
      <p className="text-xs text-text-3">
        PNG, JPG, WEBP ou SVG com até 512 KB. Formato horizontal e fundo transparente funcionam
        melhor; no menu lateral ele ocupa a largura toda, com até 64px de altura.
      </p>
    </div>
  );
}

const MODE_INFO: Record<OrganizationMode, { title: string; body: string }> = {
  AGENCY: {
    title: 'Agência',
    body: 'Atende clientes: menus Clientes, Formulários, Comercial (propostas e contratos) e Triagem, com portal do cliente. Contextos guarda os projetos, departamentos e áreas internas.',
  },
  INTERNAL: {
    title: 'Uso interno',
    body: 'Empresa organizando o próprio trabalho: as demandas se vinculam a departamentos, projetos e áreas em Contextos. Clientes, Formulários, Comercial e Triagem saem do menu.',
  },
};

/** Modo de uso: muda só o que o menu oferece; nenhum dado é apagado ao trocar. */
function ModeCard({ mode }: { mode: OrganizationMode }) {
  const toast = useToast();
  const { refetchMe } = useAuth();
  const [next, setNext] = useState<OrganizationMode>(mode);
  const [confirm, setConfirm] = useState(false);
  const save = useInvalidating(
    (m: OrganizationMode) => api.put<CompanySettings>('/organization/company/mode', { mode: m }),
    [settingsKeys.company],
  );

  return (
    <div className="flex flex-col gap-4 rounded-md border border-border bg-surface-1 p-4">
      <div>
        <LabelCaps>Modo de uso</LabelCaps>
        <p className="mt-1 text-sm text-text-3">
          Define o que aparece no menu para toda a organização. Trocar não apaga nenhum dado.
        </p>
      </div>
      <div className="grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label="Modo de uso">
        {(Object.keys(MODE_INFO) as OrganizationMode[]).map((m) => (
          <button
            key={m}
            type="button"
            role="radio"
            aria-checked={next === m}
            onClick={() => setNext(m)}
            className={cn(
              'flex flex-col gap-1 rounded border px-3 py-2.5 text-left transition-colors duration-(--dur-fast)',
              next === m
                ? 'border-accent-border bg-accent-soft'
                : 'border-border hover:border-border-strong',
            )}
          >
            <span className="flex items-center gap-2 text-sm font-medium text-text-1">
              {MODE_INFO[m].title}
              {mode === m ? <span className="font-mono text-[11px] text-text-3">atual</span> : null}
            </span>
            <span className="text-xs text-text-2">{MODE_INFO[m].body}</span>
          </button>
        ))}
      </div>
      <div className="-mx-4 -mb-4 flex justify-end border-t border-border px-4 py-3">
        <Button
          variant="primary"
          disabled={next === mode}
          loading={save.isPending}
          onClick={() => setConfirm(true)}
        >
          Aplicar
        </Button>
      </div>
      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title={`Mudar para ${MODE_INFO[next].title}?`}
        description={
          <p className="text-sm text-text-2">
            O menu muda para todas as pessoas da organização no próximo carregamento. Nenhum
            cadastro, demanda ou cliente é apagado; dá para voltar a qualquer momento.
          </p>
        }
        confirmLabel="Mudar modo"
        loading={save.isPending}
        onConfirm={() =>
          save.mutate(next, {
            onSuccess: () => {
              setConfirm(false);
              toast.success(`Modo alterado para ${MODE_INFO[next].title}`);
              void refetchMe();
            },
            onError: (err) => toast.error(err),
          })
        }
      />
    </div>
  );
}

function FaviconCard({ settings }: { settings: CompanySettings }) {
  return (
    <div className="flex flex-col gap-4 rounded-md border border-border bg-surface-1 p-4">
      <div>
        <LabelCaps>Favicon</LabelCaps>
        <p className="mt-1 text-sm text-text-3">
          Ícone da aba do navegador, no painel, no login e no portal do cliente.
        </p>
      </div>
      <LogoSlot variant="favicon" label="Favicon" logo={settings.branding.logos.favicon} />
      <p className="text-xs text-text-3">
        PNG, SVG ou ICO, quadrado, com até 512 KB. Use 512×512 px (ou SVG): o navegador reduz para
        16 e 32 px.
      </p>
    </div>
  );
}

function readBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve(typeof reader.result === 'string' ? reader.result.replace(/^data:[^,]*,/, '') : '');
    reader.onerror = () => reject(reader.error ?? new Error('Falha ao ler o arquivo.'));
    reader.readAsDataURL(file);
  });
}

function LogoSlot({
  variant,
  label,
  logo,
}: {
  variant: LogoVariant;
  label: string;
  logo: LogoInfo | null;
}) {
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const keys = [settingsKeys.company, settingsKeys.brandingAll] as const;
  const upload = useInvalidating(
    (body: { mime_type: string; data_base64: string }) =>
      api.put<CompanySettings>(`/organization/company/logos/${variant}`, body),
    keys,
  );
  const remove = useInvalidating(() => api.delete(`/organization/company/logos/${variant}`), keys);

  const favicon = variant === 'favicon';
  const accepted: readonly string[] = favicon ? FAVICON_MIME_TYPES : LOGO_MIME_TYPES;
  const onFile = async (file: File | undefined) => {
    if (!file) return;
    // .ico às vezes chega sem tipo no navegador: deduz pela extensão.
    const type = file.type || (/\.ico$/i.test(file.name) ? 'image/x-icon' : '');
    if (!accepted.includes(type)) {
      toast.error(
        null,
        favicon
          ? 'Formato não suportado. Use PNG, SVG ou ICO.'
          : 'Formato não suportado. Use PNG, JPG, WEBP ou SVG.',
      );
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      toast.error(null, 'O arquivo passa de 512 KB.');
      return;
    }
    try {
      const data_base64 = await readBase64(file);
      upload.mutate(
        { mime_type: type, data_base64 },
        {
          onSuccess: () =>
            toast.success(
              favicon ? 'Favicon atualizado' : `Logotipo do ${label.toLowerCase()} atualizado`,
            ),
          onError: (err) => toast.error(err),
        },
      );
    } catch (err) {
      toast.error(err);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-text-2">{label}</span>
        {logo ? (
          <span className="font-mono text-[11px] text-text-3">
            {logo.size < 1024 ? `${logo.size} B` : `${Math.round(logo.size / 1024)} KB`}
          </span>
        ) : null}
      </div>
      {favicon ? (
        <div className="flex h-20 items-center gap-4 rounded border border-border bg-surface-2 px-4">
          {logo ? (
            <>
              {/* Tamanhos reais da aba (16 e 32 px) e uma prévia maior. */}
              <img src={logo.url} alt="Favicon 16 px" className="size-4 object-contain" />
              <img src={logo.url} alt="Favicon 32 px" className="size-8 object-contain" />
              <img src={logo.url} alt="Favicon ampliado" className="size-12 object-contain" />
            </>
          ) : (
            <ImageOff className="size-5 text-text-3" strokeWidth={1.5} aria-label="Sem favicon" />
          )}
        </div>
      ) : (
        <div
          className="grid h-20 place-items-center rounded border border-border px-4"
          style={{
            background: variant === 'dark' ? 'var(--preview-bg-dark)' : 'var(--preview-bg-light)',
          }}
        >
          {logo ? (
            <img
              src={logo.url}
              alt={`Logotipo para ${label.toLowerCase()}`}
              className="max-h-10 max-w-full object-contain"
            />
          ) : (
            <ImageOff className="size-5 text-text-3" strokeWidth={1.5} aria-label="Sem logotipo" />
          )}
        </div>
      )}
      <div className="flex gap-2">
        <input
          ref={fileRef}
          type="file"
          accept={favicon ? `${FAVICON_MIME_TYPES.join(',')},.ico` : LOGO_MIME_TYPES.join(',')}
          className="hidden"
          onChange={(e) => {
            void onFile(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
        <Button
          type="button"
          variant="secondary"
          size="sm"
          loading={upload.isPending}
          onClick={() => fileRef.current?.click()}
        >
          <Upload /> {logo ? 'Trocar' : 'Enviar'}
        </Button>
        {logo ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            loading={remove.isPending}
            onClick={() =>
              remove.mutate(undefined, {
                onSuccess: () => toast.success(favicon ? 'Favicon removido' : 'Logotipo removido'),
                onError: (err) => toast.error(err),
              })
            }
          >
            <Trash2 /> Remover
          </Button>
        ) : null}
      </div>
    </div>
  );
}
